import { useMemo } from 'react';
import ActionLink from '../../../../Components/Dashboard/ActionLink';
import API from '../../../../Utils/api';
import useAsync from '../../../../Hooks/useAsync';
import Card from '../../../../Components/Dashboard/Card';
import Pill from '../../../../Components/Dashboard/Pill';
import { CardEmpty, CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import { weekBoundaries } from '../../../../Utils/dates/localDay';
import { formatDayMonth } from '../../../../Utils/dates/formatDate';
import styles from '../adminCards.module.css';

const TITLE = 'Feedback';
const NOTES_TITLE = 'Recent feedback';
const STATUS_TONE = { Open: 'warn', Acknowledged: 'neutral', Resolved: 'good' };
const W = 320;
const H = 90;

const FeedbackSummaryCard = ({ now: nowProp }) => {
    const now = useMemo(() => nowProp || new Date(), [nowProp]);
    const { status, data, error, reload } = useAsync(
        () => API.getAdminFeedbackSummary({ boundaries: weekBoundaries(now, 8) }).then(res => res.data),
        []
    );

    const notes = useAsync(() => API.getAdminRecentFeedback({ limit: 5 }).then(res => res.data.items), []);

    let notesBody;
    if (notes.status === 'loading' && !notes.data) notesBody = <CardLoading label={NOTES_TITLE} />;
    else if (notes.status === 'error')
        notesBody = <CardError label={NOTES_TITLE} status={notes.error?.response?.status} onRetry={notes.reload} />;
    else if (notes.data && notes.data.length === 0) notesBody = <CardEmpty>No feedback yet.</CardEmpty>;
    else if (notes.data)
        // Subject and excerpt are untrusted free text: rendered as text nodes only.
        notesBody = (
            <ul className={styles.notes} aria-label={NOTES_TITLE}>
                {notes.data.map(note => (
                    <li key={note.id}>
                        <div className={styles.noteHead}>
                            <b className={styles.noteSubject}>{note.subject || '(no subject)'}</b>
                            <Pill tone={STATUS_TONE[note.status] || 'neutral'}>{note.status}</Pill>
                        </div>
                        <p className={styles.noteText}>{note.excerpt}</p>
                        <small className={styles.noteMeta}>
                            {note.type}
                            {note.date ? ` · ${formatDayMonth(note.date)}` : ''}
                        </small>
                    </li>
                ))}
            </ul>
        );

    let body;
    let sub;
    if (status === 'loading' && !data) body = <CardLoading label={TITLE} />;
    else if (status === 'error') body = <CardError label={TITLE} status={error?.response?.status} onRetry={reload} />;
    else if (data) {
        const counts = data.buckets.map(b => b.count);
        const max = Math.max(...counts, 1);
        const slot = W / counts.length;
        sub = `${data.open} open · ${counts[counts.length - 1]} new this week`;
        const label = `New feedback per week, last ${counts.length} weeks (week starting, count): ${data.buckets
            .map(b => `${formatDayMonth(b.start)}: ${b.count}`)
            .join(', ')}`;
        let lastMonth = null;
        const axisLabels = data.buckets.map(b => {
            const [day, month] = formatDayMonth(b.start).split(' ');
            const text = month !== lastMonth ? `${day} ${month}` : day;
            lastMonth = month;
            return text;
        });
        body = (
            <>
            <svg className={styles.chart} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
                {data.buckets.map((b, i) => {
                    const h = (b.count / max) * (H - 20);
                    return (
                        <g key={b.start}>
                            <rect x={i * slot + slot * 0.2} y={H - 16 - h} width={slot * 0.6} height={h} rx="3" fill="var(--blue-700)" />
                            <text data-value x={i * slot + slot / 2} y={Math.max(H - 19 - h, 9)} textAnchor="middle">{b.count}</text>
                            <text data-axis x={i * slot + slot / 2} y={H - 3} textAnchor="middle">{axisLabels[i]}</text>
                        </g>
                    );
                })}
            </svg>
            <p className={styles.axisTitle}>Week starting (new feedback per week)</p>
            </>
        );
    }

    return (
        <Card
            id="fb"
            module="admin-feedback"
            title={TITLE}
            sub={sub}
            busy={status === 'loading'}
            right={<ActionLink to="/feedback-inspection">Open inspection</ActionLink>}
            foot={<ActionLink to="/feedback-inspection">Open all feedback</ActionLink>}
        >
            {body}
            <div className={styles.notesBlock}>{notesBody}</div>
        </Card>
    );
};

export default FeedbackSummaryCard;
